import { apiCall } from "@/common/api/ApiResult.model";
import axios from "axios";

/* 
response example:
{
	"ok": true,
	"value": {
		"externalLabelingReportRef": {
			"blobKey": "8ac14ffe-d51c-4eb7-9779-5792ae3ad9bb-mip-inmotion-v1-4c03fe8c-e293-434e-a9f1-bf37a3a8fdfd.pdf",
			"containerName": "executives-reports"
		}
	}
}
*/

/**
 * @docs - https://dev.azure.com/shieldox/Connectors/_git/integrations-manager?path=/documentation/reports.md&_a=preview
 * 0 - current month
 * 1 -  last month
 * 2 - 2 months ago
 * */
export const get_external_labeling_report_ref = apiCall<
  /**
   * `template_id` names which report the generator renders. Left out, the
   * chain falls back to what it has always produced, the pdfkit executive
   * report; "mip-labeling-report" asks for the labeling report instead.
   */
  { months_ago: number; template_id?: string },
  | { has_labeling: true; blobKey: string; containerName: string }
  | { has_labeling: false; msg: string }
>(
  "executive-reports.get_external_labeling_report_ref",
  async ({ months_ago, template_id }) => {
    const template_param = template_id ? `&templateId=${template_id}` : "";
    const res = await axios.get(
      `/extproxy/integrations/status/external-labeling-report?externalLabelProvider=mip&aggType=external-label-by-label&inverseCalendarMonthNumber=${months_ago}${template_param}`
    );
    console.log("res.data::", res.data);
    return {
      success: true,
      value: res.data.value
        ? { has_labeling: true, ...res.data.value.externalLabelingReportRef }
        : { has_labeling: false, msg: res.data.msg },
    };
  }
);

type DomainSeparatedReportRequest = {
  months_ago: number;
  orgAlias?: string;
  templateId?: "extended-report" | "short-report";
};

export const get_external_labeling_domain_separated_report_ref = apiCall<
  DomainSeparatedReportRequest,
  | { has_labeling: true; blobKey: string; containerName: string }
  | { has_labeling: false; msg: string }
>(
  "executive-reports.get_external_labeling_domain_separated_report_ref",
  async ({ months_ago, orgAlias, templateId = "extended-report" }) => {
    const alias_param = orgAlias
      ? `&orgAlias=${encodeURIComponent(orgAlias)}`
      : "";
    const template_param = templateId ? `&templateId=${templateId}` : "";
    const res = await axios.get(
      `/extproxy/integrations/status/external-labeling-domain-separated-reports?externalLabelProvider=mip&aggType=external-label-by-label&inverseCalendarMonthNumber=${months_ago}${alias_param}${template_param}`
    );
    return {
      success: true,
      value: res.data.value
        ? { has_labeling: true, ...res.data.value.externalLabelingReportRef }
        : { has_labeling: false, msg: res.data.msg },
    };
  }
);

export const get_organizations_supplementary_data = apiCall<
  { orgId: string },
  { alias?: string }
>(
  "executive-reports.get_organizations_supplementary_data",
  async ({ orgId }) => {
    const res = await axios.get(
      `/extproxy/integrations-manager/${orgId}/organizations-supplementary-data`
    );
    const alias =
      res.data?.value?.organizationsSupplementaryData?.alias ||
      res.data?.value?.organizationsSupplementaryData?.orgAlias ||
      res.data?.value?.alias;
    return {
      success: true,
      value: { alias },
    };
  }
);

/**
 * @docs - https://dev.azure.com/shieldox/Connectors/_git/integrations-manager?path=%2Fdocumentation%2Fmisc.md&_a=preview&anchor=get-blob-contents
 * */

export const generate_external_labeling_report = apiCall<
  { containerName: string; blobKey: string },
  boolean
>("reports.update_template", async ({ containerName, blobKey }) => {
  const res = await axios.get(
    `/extproxy/integrations/status/blob-contents?containerName=${containerName}&blobKey=${blobKey}`,
    {
      headers: {
        responseType: "blob",
      },
    }
  );
  return {
    success: true,
    value: res.data,
  };
});
